/// <amd-module name="Wit/LinkedWorkItemsDropdown/LinkedWorkItemsPullRequestDetailViewService" />
import { IReadyableReadonlyObservableArray, ReadyableObservableArray } from "VSS/Core/Observable";
import { IVssPageContext, IVssService, Services } from "VSS/Platform/Context";
import { routeUrl } from "VSS/Platform/Location";
import { ListBoxItemType } from "VSSUI/ListBox";
import { WorkItem, WorkItemExpand } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { LinkedWorkItemsBaseDetailViewService } from "./LinkedWorkItemsBaseDetailViewService";
import { getLinkedWorkItemsService } from "./LinkedWorkItemsService";
import { getPullRequestArtifactLink } from "./Util/Artifact";
import { workItemToListBoxItem, IWorkItemEntry } from "./Util/Data";

export interface ILinkedWorkItemsPullRequestDetailViewService extends IVssService {
    addLinkedWorkItem(workItem: WorkItem, projectId: string, repositoryId: string, pullRequestId: number): Promise<void>;

    /**
     * Loaded work items that are available to be selected from
     */
    getAvailableWorkItems(): IReadyableReadonlyObservableArray<IWorkItemEntry>;

    /**
     * To be used for filtering; contains all of the existing workItemIds linked to this PR
     * Will have no data until the array from getLinkedWorkItems is readied
     */
    getLinkedWorkItemIds(): Set<string>;

    getLinkedWorkItems(projectId: string, repositoryId: string, pullRequestId: number): IReadyableReadonlyObservableArray<IWorkItemEntry>;

    getWorkItemFormUrl(workItem: WorkItem): string;

    hydrateAvailableWorkItems(): void;

    removeLinkedWorkItem(workItem: WorkItem, projectId: string, repositoryId: string, pullRequestId: number): Promise<void>;

    updateAvailableWorkItemsForFilter(filterText: string): void;
}

const workItemFormRouteId = "ms.vss-work-web.work-items-form-route";

class LinkedWorkItemsPullRequestDetailViewService extends LinkedWorkItemsBaseDetailViewService
    implements ILinkedWorkItemsPullRequestDetailViewService {
    private availableWorkItems = new ReadyableObservableArray<IWorkItemEntry>([
        { id: "loading-row", type: ListBoxItemType.Loading, text: "loading-row" }
    ]);
    private baseAvailableWorkItems: IWorkItemEntry[] = [];
    private linkedWorkItemsHasIdBasedItem: boolean = false;

    public addLinkedWorkItem(workItem: WorkItem, projectId: string, repositoryId: string, pullRequestId: number) {
        const linkedWorkItemService = getLinkedWorkItemsService(this.pageContext);
        return linkedWorkItemService
            .addWorkItemsBatchAsync([workItem.id], [getPullRequestArtifactLink(projectId, repositoryId, pullRequestId)])
            .then(() => {
                // Query the work item to get the relation id so we can remove if necessary
                linkedWorkItemService.getWorkItem(workItem.id, this.project.name, WorkItemExpand.Relations).then((resultItem: WorkItem) => {
                    this.linkedWorkItems.push(workItemToListBoxItem(this.pageContext, resultItem));
                    this.linkedWorkItemIds.add(resultItem.id.toString());
                });
            });
    }

    public getAvailableWorkItems(): IReadyableReadonlyObservableArray<IWorkItemEntry> {
        return this.availableWorkItems;
    }

    public getLinkedWorkItemIds(): Set<string> {
        return this.linkedWorkItemIds;
    }

    public getLinkedWorkItems(projectId: string, repositoryId: string, pullRequestId: number): IReadyableReadonlyObservableArray<IWorkItemEntry> {
        return super.getLinkedWorkItems(projectId, repositoryId, pullRequestId);
    }

    public getWorkItemFormUrl(workItem: WorkItem): string {
        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = workItem.fields[CoreFieldRefNames.TeamProject];
        routeValues["id"] = workItem.id.toString();

        return routeUrl(this.pageContext, workItemFormRouteId, routeValues);
    }

    public hydrateAvailableWorkItems() {
        if (!this.availableWorkItems.ready.value) {
            const linkedWorkItemService = getLinkedWorkItemsService(this.pageContext);
            linkedWorkItemService
                .getAssignedWorkItems()
                .then(result => {
                    const assignedWorkItemIds = result.workItems.map(x => x.id);
                    if (assignedWorkItemIds.length > 0) {
                        return linkedWorkItemService.getWorkItems(assignedWorkItemIds, this.project.name, WorkItemExpand.Relations);
                    } else {
                        return [];
                    }
                })
                .then(resultItems => {
                    // Fallback when we clear the filter
                    this.baseAvailableWorkItems = resultItems.map((workItem: WorkItem) => workItemToListBoxItem(this.pageContext, workItem));
                    this.availableWorkItems.value = this.baseAvailableWorkItems;
                    this.availableWorkItems.ready.value = true;
                });
        }
    }

    public removeLinkedWorkItem(workItem: WorkItem, projectId: string, repositoryId: string, pullRequestId: number): Promise<void> {
        const workItemId = workItem.id;

        return getLinkedWorkItemsService(this.pageContext)
            .removeWorkItemsBatchAsync([workItem], [getPullRequestArtifactLink(projectId, repositoryId, pullRequestId)])
            .then(() => {
                this.linkedWorkItemIds.delete(workItemId.toString());
                this.linkedWorkItems.removeAll(x => (x.data ? x.data.id === workItemId : false));
            });
    }

    public updateAvailableWorkItemsForFilter(filterText: string) {
        const alreadyInList = this.baseAvailableWorkItems.some(item => {
            return filterText === item.id;
        });

        const id = parseInt(filterText);
        if (!isNaN(id) && !alreadyInList) {
            getLinkedWorkItemsService(this.pageContext)
                .getWorkItem(id, this.project.name)
                .then(workItem => {
                    const listBoxItem = workItemToListBoxItem(this.pageContext, workItem);
                    this.availableWorkItems.splice(0, this.linkedWorkItemsHasIdBasedItem ? 1 : 0, listBoxItem);
                    this.linkedWorkItemsHasIdBasedItem = true;
                })
                .catch(() => {
                    this.linkedWorkItemsHasIdBasedItem && this.availableWorkItems.splice(0, 1);
                    this.linkedWorkItemsHasIdBasedItem = false;
                });
        } else {
            // Remove the ID-based work item
            this.linkedWorkItemsHasIdBasedItem && this.availableWorkItems.splice(0, 1);
            this.linkedWorkItemsHasIdBasedItem = false;
        }
    }

    protected getLinkedWorkItemPromise(projectId: string, repositoryId: string, id: number): Promise<number[]> {
        const linkedWorkItemService = getLinkedWorkItemsService(this.pageContext);
        return linkedWorkItemService.getLinkedPullRequestWorkItemIds(repositoryId, id);
    }
}

const serviceName = "ILinkedWorkItemsPullRequestDetailViewService";
export function getLinkedWorkItemsPullRequestDetailViewService(pageContext: IVssPageContext) {
    return pageContext.getService<ILinkedWorkItemsPullRequestDetailViewService>(serviceName);
}

Services.add(serviceName, { serviceFactory: LinkedWorkItemsPullRequestDetailViewService });
